import { useSelector } from 'react-redux';
import type { RootState } from '@center-pwa/store/store';
import type { ActiveSubjectAdapter } from '@center-technologies/analytics';

/**
 * PWA-side adapter that surfaces the active subject / org to the shared
 * analytics provider. The shared provider calls these as hooks each render
 * and pushes the values into Statsig's user object via customIDs.
 *
 * Placeholder values today — the PWA does not yet have an explicit
 * subject-switcher UX. When that lands (e.g., a "switch subject" menu in
 * the global nav), wire the relevant Redux selector(s) here and analytics
 * will start segmenting by active subject automatically.
 *
 * Returning null from any hook is safe — userIdentity.resolveStatsigUser
 * treats null as "no active subject / org".
 */

const useActiveSubjectId = (): string | null => {
  // userProfile.userId currently corresponds to the user's identity-bound
  // subject. When subject switching UX is added, replace with whatever
  // selector reflects the chosen subject.
  return useSelector((state: RootState) => state.userProfile?.userId) ?? null;
};

const useActiveOrgId = (): string | null => {
  // No global "active org" selector exists in the PWA today; navigation
  // is sphere-driven rather than org-driven. Wire when needed.
  return null;
};

const useIsSubjectSwitched = (): boolean => {
  // No subject switching UX yet — always false.
  return false;
};

export const centerPwaActiveSubjectAdapter: ActiveSubjectAdapter = {
  useActiveSubjectId,
  useActiveOrgId,
  useIsSubjectSwitched,
};
